import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    computed,
    forwardRef,
    inject,
    viewChild
} from '@angular/core';
import { ProductListingComponentDef } from '@df/catalog-ui/product/listing/product-listing.component.def';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { AbService } from '@df/module-ab/ab.service';
import { GtmTrackViewItemListDirective } from '@df/module-gtm/track/view-item-list/gtm-track-view-item-list.directive';
import { InfiniteScrollDirective } from '@df/module-infinite-scroll/infinite-scroll.directive';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { Subscription } from 'rxjs';
import { SkeletonProductComponent } from '../../../common/skeleton-loader/product/skeleton-product.component';
import { BackToTopComponent } from '../../../layout/page/back-to-top.component';
import { CdkMultiColumnsVirtualScrollDirective } from '../../../ui/cdk/scrolling/multi-columns-virtual-scroll';
import { ProductDecorator } from '../../model/product/product.decorator';
import { ProductBannerComponent } from '../product/banner/product-banner.component';
import { ProductComponent } from '../product/product.component';
import { productColSpan } from '../product/ui/product-col-span.pipe';
import { productRowSpan } from '../product/ui/product-row-span.pipe';
import { productWidth } from '../product/ui/product-width.pipe';

@Component({
    selector: 'product-listing',
    templateUrl: './product-listing.component.html',
    styleUrl: './product-listing.component.scss',
    standalone: true,
    imports: [
        BackToTopComponent,
        CdkMultiColumnsVirtualScrollDirective,
        CommonModule,
        GridModule,
        GtmTrackViewItemListDirective,
        InfiniteScrollDirective,
        OptionLabelPipe,
        forwardRef(() => ProductBannerComponent),
        ProductComponent,
        ScrollingModule,
        SkeletonProductComponent,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListingComponent extends ProductListingComponentDef implements OnChanges, OnInit, OnDestroy {
    @Input()
    override category?: Category<ICategoryDataV3>;

    readonly virtualScroll = viewChild<CdkVirtualScrollViewport>('virtualScrollRef');

    plpProductNameVariant?: string;

    modelView = false;
    listingSize = 2;
    modelsFiltered: Product<IProductDataV2>[] = [];

    private abService = inject(AbService);
    private listingSizeSubscription?: Subscription;
    private _modelsChanged = false;

    // Cache for skeleton array
    private _skeletonArrayCache?: number[];
    private _skeletonArrayCacheKey?: string;

    get listId(): string {
        return this.category?.data?.name?.toLowerCase() || 'category';
    }

    get listName(): string {
        return this.category?.data?.name || 'Category';
    }

    // Computed signal for GTM tracking - prevents creating new object on every access
    readonly gtmTrackViewItemList = computed(() => ({
        listId: this.listId,
        listName: this.listName,
        models: this.listing?.models || []
    }));

    get displayModelViewSwitch(): boolean {
        return !!this.category?.data?.alt_view_allowed;
    }

    get productHeight(): number {
        return (this.listingSize === 2 ? (this.client.isS ? 356 : 400) : 615) + 32;
    }

    protected get isMobileiOS(): boolean {
        if (!this.app.isPlatformBrowser) return false;

        // iPhone i iPod
        if (/iPhone|iPod/.test(navigator.userAgent)) {
            return true;
        }

        // iPad
        if (/iPad/.test(navigator.userAgent) && this.client.isTouchy && (this.client.isS || this.client.isM)) {
            return true;
        }

        return false;
    }

    // Computed signal for skeleton array with memoization
    readonly skeletonArray = computed(() => {
        const productsLength = this.category?.products?.length || 0;
        const key = `${this.listingSize}-${productsLength}`;

        if (this._skeletonArrayCache && this._skeletonArrayCacheKey === key) {
            return this._skeletonArrayCache;
        }

        if (productsLength > 0) {
            const skeletons = Math.min(this.listingSize * 2, productsLength);
            this._skeletonArrayCache = Array.from({ length: skeletons }, (_, i) => i);
            this._skeletonArrayCacheKey = key;
            return this._skeletonArrayCache;
        }

        this._skeletonArrayCache = [];
        this._skeletonArrayCacheKey = key;
        return this._skeletonArrayCache;
    });

    // Computed signal for grid calculations - memoization for all products
    readonly gridCalculations = computed(() => {
        const models = (this.listing?.getSourceModels() || []) as ProductDecorator<IProductDataV2>[];
        const size = this.listingSize;

        return new Map(
            models.map(model => {
                const gridRowSpan = productRowSpan(model.rowSpan, model.colSpan, model.isLandscapeListingView) ?? 1;
                const gridColumn = productColSpan(model.colSpan, model.rowSpan, model.isBanner, size) ?? 1;
                const gridWidthValue = productWidth(model.colSpan, model.rowSpan, model.isBanner, size) ?? 12;

                return [
                    model.id,
                    {
                        gridRowSpan: gridRowSpan,
                        gridColumn: gridColumn,
                        gridWidth: gridWidthValue,
                        cssClasses: this.getGridCssClasses(gridColumn, gridRowSpan, gridWidthValue, size)
                    }
                ];
            })
        );
    });

    get nativeElement(): HTMLElement {
        return this.elementRef.nativeElement;
    }

    get canShowVirtualScroll(): boolean {
        return (
            this.client.isS && this.app.isPlatformBrowser && !(this.client.mode !== 'browser' || this.client.isBot) && !this.client.isCms
        );
    }

    trackByFn = (index: number, item: Product<IProductDataV2>) => item.id;

    // Helper to get grid calculation for specific model
    getGridCalculation(modelId: number) {
        return this.gridCalculations().get(modelId);
    }

    // Helper to generate CSS classes for grid
    private getGridCssClasses(gridColumn: number, gridRowSpan: number, gridWidth: number, size: number): string {
        const classes: string[] = [`colspan-${gridColumn}`, `rowspan-${gridRowSpan}`, `w-${gridWidth}`];

        if ((gridColumn === 3 && size === 3) || (gridColumn === 4 && size === 4)) {
            classes.push('w-12-s', 'p-t-8-x', 'p-t-8-l', 'p-b-8-x', 'p-b-8-l');
        }

        return classes.join(' ');
    }

    override ngOnInit(): void {
        this.changeDetectorRef.reattach();

        const defaultSize = this.moduleConfig.listing.category.defaultSize || [2, 2, 5, 5];

        this.listing?.size?.setDefaultSize([
            this.category?.data?.columns_s || defaultSize[0],
            this.category?.data?.columns_m || defaultSize[1],
            this.category?.data?.columns_l || defaultSize[2],
            this.category?.data?.columns_l || defaultSize[3]
        ]);

        this.listingSize = this.listing?.size?.getState() || defaultSize[this.client.sizeId];

        this.subscriptions.add(
            this.abService.getSubject('plpProductName').subscribe(value => {
                const variantId = value.variant;
                if (this.plpProductNameVariant !== variantId) {
                    this.plpProductNameVariant = variantId;
                    this.detectChanges();
                }
            })
        );
        this.plpProductNameVariant = this.abService.getVariant('plpProductName');

        super.ngOnInit();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['category']) {
            this.selectModelView(!!this.category?.data?.alt_view_default);
        }
        super.ngOnChanges(changes);
    }

    override ngOnDestroy(): void {
        this.listingSizeSubscription?.unsubscribe();

        this._skeletonArrayCache = undefined;
        this._skeletonArrayCacheKey = undefined;

        super.ngOnDestroy();
    }

    selectModelView(to: boolean): void {
        const was = this.modelView;

        if (this.displayModelViewSwitch) {
            this.modelView = to;
        } else {
            this.modelView = false;
        }

        if (was !== this.modelView) {
            this.detectChanges();
        }
    }

    onFiltersOpen() {
        this._modelsChanged = false;
    }

    onFiltersClose() {
        if (this._modelsChanged) {
            this._modelsChanged = false;
            this.returnToListingTop();
        }
    }

    onScrolledIndexChange(index: number) {
        const models = this.listing?.models || [];
        const count = Math.ceil(models.length / this.listingSize);

        if (this.listing?.hasMore && index + 5 > count) {
            this.listing?.increaseLimit();
        }
    }

    protected override onListingUpdate(): void {
        this.listingSize = this.listing?.size?.getState() || 2;

        this.modelsFiltered = this.listing?.models || [];

        super.onListingUpdate();
    }

    protected override onModelsUpdate() {
        super.onModelsUpdate();
        this._modelsChanged = true;

        this.virtualScroll()?.scrollToIndex(0);
    }
}
